import { useState, useEffect } from 'react';
import { Typography, Container, Box, Grid, Card, CardContent, Button, ListItemIcon, Modal, Alert, AlertTitle } from '@mui/material';
import {
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Assessment as ReportIcon,
  ViewList as CatalogIcon,
  FileOpenOutlined as FileOpenOutlinedIcon,
  ErrorOutline as ErrorOutlineIcon,
  AccountCircle
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { MarterIcon } from '../../../components/MarterIcon';
import { MetricCard } from './MetricCard';
import { QuickActionCard } from './QuickActionCard';
import { fetchWrapper, fetchWrapperNoAuth, fetchWrapperShipments, SERVER } from '../../../utils/helpers';
import { ErrorState } from './ErrorState';
import { useNavigate } from 'react-router-dom';
import Logo from '../../../components/Logo';
import ProductMetadataGame from '../../../components/MissingProductsGame/ProductMetadataGame';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isGameOpen, setIsGameOpen] = useState(false);

  const { theme } = useTheme();
  const { user, password, encodedAuth } = useAuth();
  const navigate = useNavigate();

  const quickActions = [
    {
      title: "Add Items",
      description: "Add new products to inventory",
      icon: <AddIcon fontSize="large" />,
      color: theme.color.primary,
      onClick: () => navigate("/add-item")
    },
    {
      title: "Upload CSV",
      description: "Bulk upload from spreadsheet",
      icon: <FileOpenOutlinedIcon fontSize="large" />,
      color: theme.accent.success,
      onClick: () => navigate("/csv-upload")
    },
    {
      title: "View Catalog",
      description: "Browse your product catalog",
      icon: <CatalogIcon fontSize="large" />,
      color: theme.color.secondary,
      onClick: () => navigate("/catalog")
    },
    {
      title: "View Reports",
      description: "Report data overview",
      icon: <ReportIcon fontSize="large" />,
      color: theme.accent.warning,
      onClick: () => navigate("/reports/supplier"),
      banner: true,
      disabled: true
    }
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      const [itemsData, shipmentsData, totalIFItems, missingItemsData] = await Promise.all([
        fetchWrapper(`${SERVER}/get_dashboard_total_items`, user, password, encodedAuth),
        fetchWrapperShipments(`${SERVER}/get_dashboard_pending_shipments`, user, password, encodedAuth),
        fetchWrapperNoAuth(`${SERVER}/get_dashboard_total_if_items/${user}`, user, password, encodedAuth),
        fetchWrapperNoAuth(`${SERVER}/get_dashboard_missing_data_items/${user}`, user, password, encodedAuth),
      ]);

      setDashboardData({
        ...itemsData,
        ...shipmentsData,
        ...totalIFItems,
        ...missingItemsData
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData({});
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setLoading(true);
    setDashboardData({});

    setTimeout(() => {
      loadData();
    }, 100);
  };

  useEffect(() => {
    loadData();
  }, []);

  if (Object.keys(dashboardData) < 1 && !loading) {
    if (encodedAuth && encodedAuth != null && encodedAuth != "" && encodedAuth != "null" && encodedAuth != "none") {
      return <>
        <ErrorState onRetry={handleRetry} />
      </>
    } else {
      return <Box sx={{
        backgroundColor: theme.background.primary,
        minHeight: '-webkit-fill-available',
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Container maxWidth="md">
          <Card sx={{
            borderRadius: '16px',
            background: theme.background.tertiary,
            border: `1px solid ${theme.border.medium}`,
            textAlign: 'center',
            p: 4
          }}>
            <CardContent>
              <ErrorOutlineIcon
                sx={{
                  fontSize: 80,
                  color: theme.accent.error,
                  mb: 2
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontFamily: theme.fontFamily,
                  color: theme.text.primary,
                  fontWeight: 'bold',
                  mb: 2
                }}
              >
                We couldn't connect to your Walmart Seller account in Inventory Falcon!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: theme.fontFamily,
                  color: theme.text.secondary,
                  mb: 3,
                  maxWidth: '500px',
                  mx: 'auto'
                }}
              >
                We're having trouble loading your data.
              </Typography>
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  textAlign: 'left',
                  backgroundColor: `${theme.accent.error}15`,
                  border: `1px solid ${theme.accent.error}30`,
                  '& .MuiAlert-message': {
                    fontFamily: theme.fontFamily,
                    color: theme.text.primary
                  },
                  '& .MuiAlert-icon': {
                    color: theme.accent.error
                  }
                }}
              >
                <AlertTitle sx={{
                  fontFamily: theme.fontFamily,
                  color: theme.text.primary,
                  fontWeight: 'bold'
                }}>
                  Go to your account details and connect your Walmart account!
                </AlertTitle>
              </Alert>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<AccountCircle />}
                  onClick={() => window.location.href = "/account"}
                  sx={{
                    fontFamily: theme.fontFamily,
                    color: theme.text.primary,
                    borderColor: theme.border.medium,
                    borderRadius: '10px',
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: theme.color.primary,
                      backgroundColor: `${theme.color.primary}10`
                    }
                  }}
                >
                  Click here to connect your Walmart account now!
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    }
  }

  return (
    <Box sx={{
      backgroundColor: theme.background.primary,
      minHeight: '-webkit-fill-available',
      p: 3
    }}>
      <Container maxWidth="xl">
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}>
          <Box>
            <Typography variant="h3" sx={{
              fontFamily: theme.fontFamily,
              color: theme.text.primary,
              fontWeight: 'bold',
              mb: 1
            }}>
              Dashboard Overview
            </Typography>
            <Typography variant="subtitle1" sx={{
              color: theme.text.secondary,
              fontFamily: theme.fontFamily
            }}>
              Welcome back! Here's what's happening with your inventory.
            </Typography>
          </Box>
        </Box>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Items"
              value={dashboardData.totalItems}
              icon={<InventoryIcon fontSize="large" />}
              color={theme.color.secondary}
              subtitle="Active in catalog"
              status="Live"
              onClick={() => navigate("/catalog")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Falcon Items"
              value={dashboardData.totalIFItems}
              icon={<Logo
                fill={theme.color.primary}
                width="47px"
                height="57px"
                background={`${theme.color.primary}05`}
              />}
              color={theme.color.primary}
              subtitle="Items added through Inventory Falcon"
              status="Updated"
              onClick={() => navigate("/catalog")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Pending Shipments"
              value={dashboardData.pendingShipments}
              icon={<ShippingIcon fontSize="large" />}
              color={theme.accent.success}
              subtitle="Ready to ship"
              status="Ready"
              onClick={() => navigate("/shipments")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Missing Data"
              value={dashboardData.missingDataItems}
              icon={<WarningIcon fontSize="large" />}
              color={
                dashboardData.missingDataItems === 0
                  ? theme.accent.success
                  : dashboardData.missingDataItems > 100
                    ? theme.accent.error
                    : theme.accent.warning
              }
              subtitle="Items with incomplete data for reports"
              status={
                dashboardData.missingDataItems === 0
                  ? "Good"
                  : dashboardData.missingDataItems > 100
                    ? "Critical"
                    : "Attention"
              }
              actionButton={{
                variant: "contained",
                title: "Fill Data",
                hide: dashboardData.missingDataItems <= 0,
                sx: {
                  backgroundColor: dashboardData.missingDataItems === 0
                    ? theme.accent.success
                    : dashboardData.missingDataItems > 100
                      ? theme.accent.error
                      : theme.accent.warning
                },
                onClick: () => {
                  setIsGameOpen(true);
                }
              }}
              {...(dashboardData.missingDataItems > 0 ? {
                onClick: () => {
                  setIsGameOpen(true);
                }
              } : {})}
            />
          </Grid>
        </Grid>
        <Card sx={{ mb: 4, borderRadius: '16px', background: theme.background.tertiary, border: `1px solid ${theme.border.medium}` }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" sx={{
                fontFamily: theme.fontFamily,
                color: theme.text.primary,
                fontWeight: 'bold',
                mb: 3
              }}>
                Quick Actions
              </Typography>
              <Button
                sx={{
                  mb: 3,
                  fontFamily: theme.fontFamily,
                  color: theme.text.primary,
                  gap: 1,
                  border: `2px solid ${theme.accent.marter}`,
                  borderRadius: "10px"
                }}
                variant="outlined"
                onClick={() => navigate("/marter-buy-list")}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: 'center',
                  }}
                >
                  <MarterIcon />
                </ListItemIcon>
                <Typography sx={{ color: theme.text.primary, fontFamily: theme.fontFamily }}>
                  Marter Buy List
                </Typography>
              </Button>
            </Box>
            <Grid container spacing={3}>
              {quickActions.map(action => (
                <Grid item xs={12} sm={6} md={3}>
                  <QuickActionCard
                    title={action.title}
                    description={action.description}
                    icon={action.icon}
                    color={action.color}
                    onClick={action.onClick}
                    banner={action.banner}
                    disabled={action.disabled}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Container>
      <Modal
        open={isGameOpen}
        onClose={() => setIsGameOpen(false)}
        sx={{ backdropFilter: 'blur(3px)' }}
      >
        <Box sx={{
          outline: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: 2
        }}>
          <ProductMetadataGame isOpen={isGameOpen} onClose={() => setIsGameOpen(false)} />
        </Box>
      </Modal>
    </Box>
  );
};

export default Dashboard;